/* eslint-disable max-lines */
import React, { lazy, Suspense, useEffect } from 'react'
import { Switch, Redirect, Route } from 'react-router-dom'

import { Structure } from 'components/structure'
import { Loader } from 'components/loader'

import { useMessage } from 'hooks/use-message'
import { redirectLastRoute } from 'hooks/use-last-route'
import * as microfrontends from 'pages/microfrontends'

import { actionProductFruits } from 'helpers/product-fruits'
import PrivateRoute from './private-route.component'
import { PLATFORM_ROUTE_GROUPS, APP_ROUTES } from './routes.constants'

const PlatformManager = lazy(() => import('./platform-manager.component'))
const Club = lazy(() => import('pages/club/club.component'))
const NotFound = lazy(() => import('pages/not-found'))
const Platform = lazy(() => import('pages/platform/platform.component'))
const Unknown = lazy(() => import('components/unknown'))
const Password = lazy(() => import('pages/password'))

const renderWallet = () => (
  <>
    <microfrontends.Payouts />
    <microfrontends.AuthWallet />
  </>
)

// eslint-disable-next-line max-lines-per-function
const Routes = () => {
  const { initMessageListener } = useMessage()

  useEffect(() => {
    initMessageListener()
    actionProductFruits('add')
    return () => {
      actionProductFruits('remove')
    }
  }, [])

  return (
    <Suspense fallback={<Loader />}>
      <Structure>
        <Switch>
          <PrivateRoute
            exact
            path={[APP_ROUTES.root, APP_ROUTES.dashboard, APP_ROUTES.dashboardInstagramIntegration]}
            component={microfrontends.Dashboard}
          />
          <PrivateRoute exact path={APP_ROUTES.club.product} component={Club} />
          <PrivateRoute exact path={APP_ROUTES.membersArea} component={microfrontends.MembersArea} />
          <PrivateRoute
            exact
            path={APP_ROUTES.boostSalesDashboard.salesStrategy}
            component={microfrontends.BoostSalesStrategy}
          />
          <PrivateRoute
            exact
            path={APP_ROUTES.boostSalesDashboard.salesDashboards.management}
            component={microfrontends.BoostSalesManagement}
          />
          <PrivateRoute
            exact
            path={APP_ROUTES.boostSalesDashboard.salesDashboards.myDashboard}
            component={microfrontends.BoostSalesManagementMyDashbaord}
          />
          <PrivateRoute
            exact
            path={APP_ROUTES.boostSalesDashboard.chargebackRate}
            component={microfrontends.BoostSalesChargeBack}
          />
          <PrivateRoute
            exact
            path={APP_ROUTES.boostSalesDashboard.metrics}
            component={microfrontends.BoostSalesMetrics}
          />
          <PrivateRoute exact path={APP_ROUTES.tools.kajabi} component={microfrontends.Kajabi} />
          <PrivateRoute exact path={APP_ROUTES.tools.pixel} component={microfrontends.Pixel} />
          <PrivateRoute exact path={APP_ROUTES.tools.driveReport} component={microfrontends.DriveReport} />
          <PrivateRoute exact path={APP_ROUTES.tools.integrations} component={() => <microfrontends.Integration />} />
          <PrivateRoute
            exact
            path={APP_ROUTES.sales.smartInstallment}
            component={microfrontends.SmartInstallmentReport}
          />
          <PrivateRoute exact path={APP_ROUTES.sales.smartRecovery} component={microfrontends.SmartRecoveryReport} />
          <PrivateRoute
            exact
            path={APP_ROUTES.subscription.dashboard}
            component={microfrontends.SubscriptionDashboard}
          />
          <PrivateRoute
            exact
            path={APP_ROUTES.subscription.subscriptionManagement}
            component={microfrontends.SubscriptionManagement}
          />
          <PrivateRoute
            path={APP_ROUTES.subscription.retentionStrategies.strategiesDashboard}
            component={microfrontends.SubscriptionRetentionStrategies}
          />
          <PrivateRoute
            path={APP_ROUTES.subscription.retentionStrategies.earlyRenewal.details}
            component={microfrontends.SubscriptionRetentionStrategies}
          />
          <PrivateRoute
            path={APP_ROUTES.subscription.retentionStrategies.earlyRenewal.new}
            component={microfrontends.SubscriptionRetentionStrategies}
          />
          <PrivateRoute
            path={[APP_ROUTES.subscription.deprecatedStatus, APP_ROUTES.subscription.status]}
            component={microfrontends.SubscriptionStatus}
          />
          <PrivateRoute
            path={APP_ROUTES.subscription.templateDashboard}
            component={microfrontends.SubscriptionStatus}
          />
          <PrivateRoute path={APP_ROUTES.subscription.rfm} component={microfrontends.SubscriptionRFM} />
          <PrivateRoute path={APP_ROUTES.marketplace.analytics} component={microfrontends.MarketplaceAnalytics} />
          <PrivateRoute path={APP_ROUTES.subscription.marketAnalysis} component={microfrontends.SubscriptionStatus} />
          <PrivateRoute path={APP_ROUTES.subscription.tools} component={microfrontends.SubscriptionsHubTools} />

          <PrivateRoute exact path={APP_ROUTES.chat} component={microfrontends.CommunicationChat} />
          <PrivateRoute exact path={APP_ROUTES.documents} component={microfrontends.Documents} />
          <PrivateRoute
            exact
            path={[
              APP_ROUTES.userAccounts.personal.updatePersonalData,
              APP_ROUTES.userAccounts.personal.updateDocuments
            ]}
            component={microfrontends.UserAccountPersonal}
          />
          <PrivateRoute exact path={APP_ROUTES.blackNovember} component={microfrontends.BlackNovember} />
          <PrivateRoute exact path={APP_ROUTES.membership} component={microfrontends.MembershipAdmin} />
          <PrivateRoute exact path={APP_ROUTES.tools.automations} component={microfrontends.WhatsappAutomations} />
          <PrivateRoute exact path={APP_ROUTES.tools.kajabi} component={microfrontends.Kajabi} />
          <PrivateRoute exact path={APP_ROUTES.tools.pixel} component={microfrontends.Pixel} />
          <PrivateRoute exact path={APP_ROUTES.tools.integrations} component={microfrontends.Integration} />
          <PrivateRoute exact path={APP_ROUTES.tools.webhook} component={microfrontends.ConnectAppWebHook} />
          <PrivateRoute exact path={APP_ROUTES.tools.credentials} component={microfrontends.AppCredentials} />
          <PrivateRoute exact path={APP_ROUTES.onboarding} component={microfrontends.Onboarding} />
          <PrivateRoute exact path={APP_ROUTES.tools.supportConfig} component={microfrontends.WhatsappSupportConfig} />
          <PrivateRoute
            exact
            path={[
              APP_ROUTES.tools.audience,
              APP_ROUTES.tools.list.affiliate,
              APP_ROUTES.tools.list.producer,
              APP_ROUTES.tools.list.coproducer,
              APP_ROUTES.tools.list.home,
              `${APP_ROUTES.tools.list.home}${APP_ROUTES.tools.list.list}`,
              APP_ROUTES.tools.productSelection,
              APP_ROUTES.tools.productSelectionGroup
            ]}
            component={() => <microfrontends.PlatformTools />}
          />
          <PrivateRoute exact path={APP_ROUTES.tools.webhook} component={microfrontends.ConnectAppWebHook} />
          <PrivateRoute exact path={APP_ROUTES.tools.credentials} component={microfrontends.AppCredentials} />
          <PrivateRoute
            exact
            path={[
              APP_ROUTES.products.home,
              APP_ROUTES.products.affiliations,
              APP_ROUTES.products.coproduction,
              APP_ROUTES.products.producer
            ]}
            component={() => <microfrontends.Product />}
          />

          <PrivateRoute
            exact
            path={[
              APP_ROUTES.pro.start,
              APP_ROUTES.pro.professionals,
              APP_ROUTES.pro.professionalDetails,
              APP_ROUTES.pro.partnerships,
              APP_ROUTES.pro.partnershipsCreate,
              APP_ROUTES.pro.partnershipRating,
              APP_ROUTES.pro.demands,
              APP_ROUTES.pro.chatRedirect,
              APP_ROUTES.pro.bundles,
              APP_ROUTES.pro.bundleDetails,
              APP_ROUTES.pro.briefing
            ]}
            component={microfrontends.HotmartPRO}
          />

          <PrivateRoute
            exact
            path={[APP_ROUTES.affiliation.hotlinks, APP_ROUTES.affiliation.changeHistory]}
            component={microfrontends.Affiliation}
          />

          <PrivateRoute path={PLATFORM_ROUTE_GROUPS} component={PlatformManager} />
          <PrivateRoute exact path={APP_ROUTES.market} component={microfrontends.Market} />
          <PrivateRoute exact path={APP_ROUTES.wallet} component={() => renderWallet()} />
          <PrivateRoute exact path={APP_ROUTES.cas.renew} component={Loader} />
          <Redirect from={APP_ROUTES.login} to={redirectLastRoute()} />
          <Redirect from={APP_ROUTES.signup} to={redirectLastRoute()} />
          <Redirect from={APP_ROUTES.cas.login} to={redirectLastRoute()} />
          <Redirect from={APP_ROUTES.cas.logout} to={redirectLastRoute()} />
          <Redirect from={APP_ROUTES.cas.surrogate} to={redirectLastRoute()} />
          <Route from="*" exact component={Unknown} />
        </Switch>

        <Platform />
        <microfrontends.BlipChatComponent />
      </Structure>
      <Route exact path={APP_ROUTES.notFound} component={NotFound} />
      <Route exact path={Object.values(APP_ROUTES.password)} render={props => <Password {...props} />} />
    </Suspense>
  )
}

export default Routes
